(ns dashboard.entity.document.js-events
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require [clojure.string :as str]
            [dashboard.entity.document.js-dom :as js.dom]))

;; ----------------------------------------------------------------------------
;;  Here's where the magic happens... :( ...  #sadreactsonly
;; ----------------------------------------------------------------------------
;; In order to be able to detect when the user has hovered over a previously-
;; highlighted piece of evidence, and then figure out which piece of evidence,
;; we are doing some CSS magic that takes the following form:
;; Normally, we display the HTML canvas with the hidden text layer on top.
;; When we capture highlighted text as evidence, we then display the highlights
;; on top of the hidden text. This allows us, on mouse-over, to capture the ID
;; and page # of the evidence from the dom node name.
;; That said, when we select text, we want to make a contunuous selection across
;; previously highlighted text and into un-highlighted reigions. So, in order to
;; not have the overlaying highlight text take the mouse events from the text
;; layer underneath, we have a mouse-down event that will update the CSS
;; stylesheet class for the text layer to set pointer-events to NONE, allowing
;; the selection to pass through to the text layer.
;;
;; Do we want the highlight on top (z dim), in order to pin mouseover to an
;; evidence ID? Do we want the text layer on top to allow the user to select
;; passage text that has already been selected AND text outside the pre-selected
;; range into a single selection? Yes to both, at the same time...
;;
;; What we should explore is the idea of giving the user a MODE toggle where:
;; - when ON, the user can select text from the document
;; - when OFF, the user can review the evidence contained within the document
;;
;; Doing that will make the user interaction state obvious, and we can get rid
;; of 90% of this weird code - and the UI will be faster - and we will not have
;; to add a swath of event handlers to the js/document object.
;; ----------------------------------------------------------------------------

(defonce selection-change-debounce-timer (atom nil))

(defn handle-selection-change
  "Handles changes to selected text on the entire window."
  [set-selected-text set-selection-range pdf-ref]
  (let [handler (fn []
                  (let [selection (.getSelection js/window)
                        selected-text (.toString selection)
                        start (-> selection
                                  .-anchorNode
                                  js.dom/grandchild->page-number)
                        end (-> selection
                                .-focusNode
                                js.dom/grandchild->page-number)]
                    (when (and start
                               end
                               (.contains pdf-ref (-> (.getSelection js/window)
                                                      .-anchorNode)))
                      (set-selection-range
                       {:start start
                        :end end
                        :html (js.dom/get-selected-html selection)}))
                    (set-selected-text selected-text)
                    (reset! selection-change-debounce-timer nil)))]
    (when (.contains pdf-ref (-> (.getSelection js/window)
                                 .-anchorNode))
     (if @selection-change-debounce-timer
      (js/clearTimeout @selection-change-debounce-timer)
      (handler))

      (reset! selection-change-debounce-timer (js/setTimeout handler 300)))))

(defonce mouse-over-debounce-timer (atom nil))

(defn handle-mouse-over
  "On-mouse over we determine which (if any) piece of evidence the user has
   moused over. Currently we set the active highlight (evidence) based on
   the mouse position, and we update the location of the highlight-info--pointer
   to match the y-pos of the user cursor, denoting the active evidence.
   Likewise we assign the highlight-info--active-highlight to the active highlight"
  [set-active-highlight e]
  (when @mouse-over-debounce-timer
    (js/clearTimeout @mouse-over-debounce-timer))

  (reset! mouse-over-debounce-timer
          (js/setTimeout
           (fn []
             (when-let [highlight-id (aget e.target "data")]
               (let [is-evidence? (not (str/includes? e.target.className "paragraph"))
                     element (if is-evidence?
                               e.target.parentElement
                               e.target)]

                 (set-active-highlight
                  {(if is-evidence?
                     :evidence-id
                     :document-paragraph-id) highlight-id
                   :type (if is-evidence? :evidence :paragraph)})

                 (js.dom/remove-class-from-all "highlight-info--active-highlight")

                 (-> element
                     .-className
                     (set! (str element.className
                                " "
                                "highlight-info--active-highlight"))))))
           300))) ;; 300ms debounce delay


(defonce mouse-down-debounce-timer (atom nil))

(defn handle-mouse-down
  "On-click, we disable pointer events from the highlighted evidence text, allowing
   user selection to hit the document text layer underneath"
  [e]
  (if @mouse-down-debounce-timer
    (js/clearTimeout @mouse-down-debounce-timer)
    (do
      (js.dom/update-css-class "highlightLayer" {"pointer-events" "none"})
      (js.dom/update-css-class "paragraphsLayer" {"pointer-events" "none"})))

  (reset! mouse-down-debounce-timer
          (js/setTimeout
           (fn []
             (js.dom/update-css-class "highlightLayer" {"pointer-events" "none"})
             (js.dom/update-css-class "paragraphsLayer" {"pointer-events" "none"})
             (reset! mouse-down-debounce-timer nil))
           30)))


(defn bind-event-handlers
  "Attach event handlers to the window and return a function for removing said handlers"
  [set-selected-text
   set-selection-range
   set-active-highlight
   pdf-ref]
  (let [handle-key-down (fn [e]
                          (when e.shiftKey
                            (js.dom/update-css-class "highlightLayer" {"pointer-events" "none"})
                            (js.dom/update-css-class "paragraphsLayer" {"pointer-events" "none"})))
        handle-mouse-up (fn []
                          (js.dom/update-css-class "highlightLayer" {"pointer-events" "all"})
                          (js.dom/update-css-class "paragraphsLayer" {"pointer-events" "all"}))
        handle-selection-change (partial handle-selection-change
                                         set-selected-text
                                         set-selection-range
                                         pdf-ref)
        handle-mouse-over (partial handle-mouse-over set-active-highlight)]
    (js/document.addEventListener "selectionchange" handle-selection-change)
    (js/document.addEventListener "mousemove" handle-mouse-over)
    (js/document.addEventListener "mousedown" handle-mouse-down)
    (js/document.addEventListener "mouseup" handle-mouse-up)
    (js/document.addEventListener "keydown" handle-key-down)
    (js/document.addEventListener "click" handle-mouse-over)
    (fn []
      (js/document.removeEventListener "selectionchange" handle-selection-change)
      (js/document.removeEventListener "mousemove" handle-mouse-over)
      (js/document.removeEventListener "mousedown" handle-mouse-down)
      (js/document.removeEventListener "mouseup" handle-mouse-up)
      (js/document.removeEventListener "keydown" handle-key-down)
      (js/document.removeEventListener "click" handle-mouse-over))))